/**
 * Confirmación para HABILITAR a un usuario (`active: true`), en dos variantes:
 *
 *   mode="accept"  → aceptar un repartidor de la cola "Por aceptar". Además de
 *                    activarlo lo vincula al operador (ver acceptDeliveryUserApi).
 *   mode="unblock" → desbloquear a un usuario ya vinculado. Solo `active: true`.
 *
 * Existe porque el menú de fila enrutaba el "Desbloquear" a
 * BlockDeleteUserBeartrack, que con `block=false` NO desbloquea: borra la cuenta
 * (DELETE delete-user/:id → findByIdAndDelete, sin soft-delete). Este modal es el
 * camino de habilitación que faltaba.
 *
 * Sin checkbox de confirmación a propósito: habilitar es reversible (se puede
 * volver a bloquear), a diferencia del borrado. Pedir el mismo ritual para ambas
 * acciones es lo que entrena al usuario a marcar la casilla sin leer.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserCheck, Unlock } from "lucide-react";
import { toast } from "../../utils/toast";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Avatar } from "../ui/Avatar";
import { acceptDeliveryUserApi, activateUserApi } from "../../api/user";

const initialsOf = (n, l) =>
  `${(n || "").charAt(0)}${(l || "").charAt(0)}`.toUpperCase() || "?";

export default function EnableUserModal({
  open,
  onClose,
  user,
  mode = "accept",
  logisticCompanyId,
  onDone,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const isAccept = mode === "accept";
  // El repartidor solo queda operativo si podemos vincularlo a un operador. Sin
  // él, el PATCH saldría incompleto (o lo rechazaría el DTO), así que la acción
  // se bloquea acá con un motivo visible en vez de fallar contra el backend.
  const missingOperator = isAccept && !(logisticCompanyId?._id ?? logisticCompanyId);

  const handleConfirm = async () => {
    if (loading || missingOperator || !user?._id) return;
    setLoading(true);

    const { error } = isAccept
      ? await acceptDeliveryUserApi({ userId: user._id, logisticCompanyId })
      : await activateUserApi({ userId: user._id, status: true });

    setLoading(false);

    if (error) {
      toast.error(
        error.message ||
          (isAccept
            ? t("No se pudo habilitar al repartidor")
            : t("No se pudo desbloquear al usuario")),
      );
      return;
    }

    toast.success(
      isAccept ? t("Repartidor habilitado") : t("Usuario desbloqueado"),
    );
    onClose?.();
    onDone?.();
  };

  const fullName = [user?.name, user?.lastName].filter(Boolean).join(" ");

  return (
    <Modal
      open={open}
      onClose={() => !loading && onClose?.()}
      size="sm"
      title={isAccept ? t("Habilitar repartidor") : t("Desbloquear usuario")}
      footer={
        <div className="flex items-center justify-end w-full gap-2">
          <Button variant="outline" onClick={() => onClose?.()} disabled={loading}>
            {t("Cancelar")}
          </Button>
          <Button
            onClick={handleConfirm}
            loading={loading}
            disabled={missingOperator}
            iconLeft={
              isAccept ? <UserCheck className="w-4 h-4" /> : <Unlock className="w-4 h-4" />
            }
          >
            {isAccept ? t("Habilitar repartidor") : t("Desbloquear usuario")}
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-3 rounded-xl border border-border bg-bg-muted/50 px-3 py-2.5 mb-4">
        <Avatar initials={initialsOf(user?.name, user?.lastName)} size="sm" tone="brand" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-fg truncate">{fullName || "—"}</div>
          <div className="text-xs text-fg-muted truncate">{user?.email || "—"}</div>
        </div>
      </div>

      <p className="text-[13px] text-fg-muted">
        {isAccept
          ? t("Quedará habilitado para trabajar con tu operación y podrá iniciar sesión en la app de repartos con sus credenciales.")
          : t("Recuperará el acceso a la plataforma con sus credenciales actuales.")}
      </p>

      {missingOperator && (
        <p className="text-[13px] text-status-error mt-3">
          {t("No tienes empresa logística asociada para asignar.")}
        </p>
      )}

      <p className="text-xs text-fg-subtle mt-3">
        {t("Podrás volver a bloquearlo cuando quieras desde este mismo menú.")}
      </p>
    </Modal>
  );
}
